import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import { SaaSGamaLogo } from "@/components/ui/saasgama-logo";
import { Shield, Lock, Eye, Server, FileCheck, Globe } from "lucide-react";
import { WebsiteFooter } from "@/components/layout/website-footer";

export default function SecurityPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <Helmet>
        <title>Security & Trust | SaaSGama Data Protection</title>
        <meta name="description" content="Learn how SaaSGama protects your data with enterprise-grade security, encryption, GDPR compliance, and industry best practices." />
        <meta name="keywords" content="SaaSGama Security, Data Protection, GDPR Compliance, SaaS Security, Enterprise Security, Data Encryption" />
        <link rel="canonical" href="https://saasgama.com/security" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://saasgama.com/security" />
        <meta property="og:site_name" content="SaaSGama" />
        <meta property="og:title" content="Security & Trust | SaaSGama Data Protection" />
        <meta property="og:description" content="Learn how SaaSGama protects your data with enterprise-grade security, encryption, GDPR compliance, and industry best practices." />
        <meta property="og:image" content="https://saasgama.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Security & Trust | SaaSGama Data Protection" />
        <meta name="twitter:description" content="Learn how SaaSGama protects your data with enterprise-grade security, encryption, GDPR compliance, and industry best practices." />
        <meta name="twitter:image" content="https://saasgama.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "WebPage", "@id": "https://saasgama.com/security/#webpage", "url": "https://saasgama.com/security", "name": "Security & Trust | SaaSGama", "description": "Learn how SaaSGama protects your data with enterprise-grade security, encryption, and compliance." },
            { "@type": "BreadcrumbList", "@id": "https://saasgama.com/security/#breadcrumb", "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://saasgama.com" },
              { "@type": "ListItem", "position": 2, "name": "Security", "item": "https://saasgama.com/security" }
            ]}
          ]
        })}</script>
      </Helmet>

      {/* Navigation */}
      <header className="fixed w-full bg-white/90 backdrop-blur-md z-50 border-b border-gray-100">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <Link href="/"><SaaSGamaLogo size="lg" /></Link>
            <nav className="hidden md:flex space-x-8">
              <Link href="/features" className="text-gray-600 hover:text-blue-600 transition-colors font-medium">Features</Link>
              <Link href="/pricing" className="text-gray-600 hover:text-blue-600 transition-colors font-medium">Pricing</Link>
              <Link href="/about" className="text-gray-600 hover:text-blue-600 transition-colors font-medium">About</Link>
              <Link href="/blog" className="text-gray-600 hover:text-blue-600 transition-colors font-medium">Blog</Link>
              <Link href="/contact" className="text-gray-600 hover:text-blue-600 transition-colors font-medium">Contact</Link>
            </nav>
            <div className="flex items-center gap-3">
              <Link href="/login" className="text-gray-600 hover:text-blue-600 font-medium">Sign In</Link>
              <Link href="/register" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium">Get Started</Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Shield className="w-4 h-4" />
            Enterprise-Grade Security
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Your Data is Safe with SaaSGama
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            We take security seriously. SaaSGama is built with industry-leading practices to protect your organization's data at every layer.
          </p>
        </div>
      </section>

      {/* Security pillars */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Lock, title: "Data Encryption", desc: "All data is encrypted in transit using TLS 1.2+ and at rest using AES-256 encryption. Your sensitive information is never stored in plain text." },
              { icon: Eye, title: "Access Controls", desc: "Role-based access control (RBAC) ensures users only see what they need. Multi-factor authentication (MFA) adds an extra layer of protection." },
              { icon: Server, title: "Secure Infrastructure", desc: "Hosted on enterprise-grade cloud infrastructure with 99.9% uptime SLA, automatic backups, and disaster recovery procedures." },
              { icon: FileCheck, title: "GDPR Compliant", desc: "SaaSGama is fully GDPR compliant. We provide data processing agreements, right-to-erasure workflows, and data portability on request." },
              { icon: Globe, title: "PDPA Ready", desc: "For organizations operating in Southeast Asia, SaaSGama supports PDPA compliance requirements including consent management and data subject rights." },
              { icon: Shield, title: "Continuous Monitoring", desc: "24/7 automated security monitoring, vulnerability scanning, and incident response procedures keep your data protected around the clock." },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance */}
      <section className="py-16 px-6 bg-white">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">Compliance & Standards</h2>
          <p className="text-gray-600 text-center mb-12">SaaSGama is designed to help your organization meet regulatory requirements.</p>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { label: "GDPR", desc: "General Data Protection Regulation — EU data privacy compliance." },
              { label: "PDPA", desc: "Personal Data Protection Act — Southeast Asia data compliance." },
              { label: "Data Residency", desc: "Control where your data is stored with region selection." },
              { label: "Audit Logs", desc: "Complete audit trail of all user actions for compliance reporting." },
              { label: "SSO / SAML", desc: "Single Sign-On integration with your existing identity provider." },
              { label: "Data Retention", desc: "Configurable data retention policies to meet your legal obligations." },
            ].map(({ label, desc }) => (
              <div key={label} className="flex items-start gap-4 p-4 rounded-lg border border-gray-100">
                <div className="w-8 h-8 bg-green-50 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">{label}</div>
                  <div className="text-sm text-gray-600">{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Security FAQs</h2>
          <div className="space-y-6">
            {[
              { q: "Who can access my organization's data?", a: "Only authenticated users within your organization can access your data. SaaSGama staff never access customer data without explicit written permission for support purposes." },
              { q: "Where is my data stored?", a: "Data is stored on secure cloud infrastructure. Enterprise customers can request specific region data residency." },
              { q: "What happens to my data if I cancel?", a: "Upon cancellation, your data is retained for 30 days to allow export, then permanently deleted from all systems." },
              { q: "Do you perform security audits?", a: "Yes. We conduct regular internal security reviews and vulnerability assessments to maintain the highest security standards." },
              { q: "How do I report a security vulnerability?", a: "Please contact us at security@saasgama.com. We take all reports seriously and respond within 24 hours." },
            ].map(({ q, a }) => (
              <div key={q} className="bg-white rounded-xl border border-gray-100 p-6">
                <h3 className="font-semibold text-gray-900 mb-2">{q}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-blue-600">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Questions about security?</h2>
          <p className="text-blue-100 mb-8">Our team is happy to answer any security or compliance questions before you get started.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/contact" className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors">Contact Us</Link>
            <Link href="/register" className="border border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">Start Free Trial</Link>
          </div>
        </div>
      </section>

      <WebsiteFooter />
    </div>
  );
}
